import React, { createContext, ReactNode, useCallback, useMemo } from 'react';
import { Events } from 'src/graphql/schema.graphql';
import { useTimeState } from 'src/hooks/useTimeState';
import { useQueryClient } from 'react-query';
import { QueryKey } from 'src/models/query';

export interface EventPageContextProviderProps {
  event: Events;
  children: ReactNode;
}

export interface EventPageContextValue {
  event: Events;
  isUpcoming: boolean;
  isNow: boolean;
  isCompleted: boolean;
}

export const EventPageContext = createContext<EventPageContextValue>(
  {} as EventPageContextValue,
);

export const EventPageContextProvider = ({
  event,
  children,
}: EventPageContextProviderProps) => {
  const queryClient = useQueryClient();

  const onStart = useCallback(
    () => queryClient.refetchQueries([...QueryKey.EventPage, event.slug]),
    [event.slug, queryClient],
  );

  const { isUpcoming, isNow, isCompleted } = useTimeState(event, onStart);

  const value = useMemo(
    () => ({
      event,
      isNow,
      isUpcoming,
      isCompleted,
    }),
    [event, isCompleted, isNow, isUpcoming],
  );

  return (
    <EventPageContext.Provider value={value}>
      {children}
    </EventPageContext.Provider>
  );
};
